import upperFirst from 'lodash/upperFirst'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { GraphBucketWithTotalResponse, GraphCompareWithTotalResponse, GraphStackResponse } from 'src/api/gen'
import { exportTripsPerformed, formatExportComparing, formatExportTotal } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import { Can, userHasPermission } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useProviders, useProviderUser } from 'src/layout/hooks'

import { scrollToComponent } from '../hooks'

import { UniqueUserPerDay, UniqueUserPerProvider, UserPerTripsPerformed } from './charts'

export function UniqueUserComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [comparing, setComparing] = useState<boolean>(false)
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  const title = comparing ? upperFirst(t('kpi.UniqueUser.title.comparing')) : upperFirst(t('kpi.UniqueUser.title'))
  const description = comparing
    ? upperFirst(t('kpi.UniqueUser.description.comparing'))
    : upperFirst(t('kpi.UniqueUser.description'))

  async function fetcher(signal: AbortSignal): Promise<GraphBucketWithTotalResponse | GraphCompareWithTotalResponse> {
    const data = createPayloadFromFilters(props.filters)
    if (comparing) {
      return await api.graphs.graphNumberOfUniqueUsersPerProvider({ data, options: { signal } })
    } else {
      return await api.graphs.graphNumberOfUniqueUsers({ data, options: { signal } })
    }
  }

  function render(data: GraphBucketWithTotalResponse | GraphCompareWithTotalResponse, title?: string) {
    if (comparing) {
      return (
        <UniqueUserPerProvider
          title={title}
          graphReport={data as GraphCompareWithTotalResponse}
          granularity={props.filters.granularity}
        />
      )
    }
    return (
      <UniqueUserPerDay graphReport={data as GraphBucketWithTotalResponse} granularity={props.filters.granularity} />
    )
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = comparing
      ? formatExportComparing(props.filters.granularity)
      : formatExportTotal(props.filters.granularity, t('kpi.export.UniqueUsers'))
  }

  return (
    <DashboardGraphWrapper
      id={props.id}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      toggles={
        isProviderUser && activeProviders.length < 2
          ? []
          : [
              {
                active: comparing,
                onToggle: () => setComparing(!comparing),
                activeIcon: 'toggle-off',
                inactiveIcon: 'toggle-on',
                activeText: t('kpi.UniqueUser.Toggle.activeText'),
                inactiveText: t('kpi.UniqueUser.Toggle.inactiveText'),
              },
            ]
      }
      updateOn={[comparing, props.filters]}
    />
  )
}

export function NewUserComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [comparing, setComparing] = useState<boolean>(false)
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  const title = comparing ? upperFirst(t('kpi.NewUser.title.comparing')) : upperFirst(t('kpi.NewUser.title'))
  const description = comparing
    ? upperFirst(t('kpi.NewUser.description.comparing'))
    : upperFirst(t('kpi.NewUser.description'))

  async function fetcher(signal: AbortSignal): Promise<GraphBucketWithTotalResponse | GraphCompareWithTotalResponse> {
    const data = createPayloadFromFilters(props.filters)
    if (comparing) {
      return await api.graphs.graphNumberOfNewUsersPerProvider({ data, options: { signal } })
    } else {
      return await api.graphs.graphNumberOfNewUsers({ data, options: { signal } })
    }
  }
  function render(data: GraphBucketWithTotalResponse | GraphCompareWithTotalResponse, title?: string) {
    if (comparing) {
      return (
        <UniqueUserPerProvider
          title={title}
          graphReport={data as GraphCompareWithTotalResponse}
          granularity={props.filters.granularity}
        />
      )
    }
    return (
      <UniqueUserPerDay
        title={title}
        graphReport={data as GraphBucketWithTotalResponse}
        granularity={props.filters.granularity}
      />
    )
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = comparing
      ? formatExportComparing(props.filters.granularity)
      : formatExportTotal(props.filters.granularity, t('kpi.export.NewUsers'))
  }

  return (
    <DashboardGraphWrapper
      id={props.id}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      toggles={
        isProviderUser && activeProviders.length < 2
          ? []
          : [
              {
                active: comparing,
                onToggle: () => setComparing(!comparing),
                activeIcon: 'toggle-off',
                inactiveIcon: 'toggle-on',
                activeText: t('kpi.UniqueUser.Toggle.activeText'),
                inactiveText: t('kpi.UniqueUser.Toggle.inactiveText'),
              },
            ]
      }
      updateOn={[comparing, props.filters]}
    />
  )
}

function UserPerTripPerformedComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(signal: AbortSignal): Promise<GraphStackResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphNumberOfUsersPerTripPerformed({ data, options: { signal } })
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = exportTripsPerformed()
  }

  function render(data: GraphStackResponse[], title?: string) {
    return <UserPerTripsPerformed title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  return (
    <DashboardGraphWrapper
      title={upperFirst(t('kpi.TripsPerformedPerUser.title'))}
      description={upperFirst(t('kpi.TripsPerformedPerUser.description'))}
      fetcher={fetcher}
      render={render}
      customExport={formatExportBuckets}
      updateOn={[props.filters]}
    />
  )
}

interface IUsersChartsProps {
  filters: IFilters
}

function UsersCharts(props: IUsersChartsProps) {
  useEffect(scrollToComponent)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Can I="summarize" an="kpi">
        <UniqueUserComponent id="unique-users" filters={props.filters} />
        <NewUserComponent id="new-users" filters={props.filters} />
        <UserPerTripPerformedComponent id="users-per-trip-performed" filters={props.filters} />
      </Can>
    </BaseChartWrapper>
  )
}

export default UsersCharts
